import React, { useEffect, useState } from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { CheckIcon, ArrowUUpLeftIcon, DownloadIcon } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useExportTwilioChatCSV } from '@/api/chat'
import { ZoomFrame } from '@/components/ui/zoomable-image'

import Icon from '../../Icons'
import StatusPill from '../../StatusPill/StatusPill'
import InformationPopup from '../../Popups/InformationPopup'
import { getFormatedFullDate } from '../../../helpers/helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'
import {
  isMaintenanceRequestChatType,
  isMaintenanceGroupChatType,
  renderProperStatusPill,
  getMaintenanceRequestId,
  conversationStatusMap,
  getUserNameById,
} from '../helpers'
import API from '../../../api/api'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'

const ConversationInfoBar = ({
  selectedChat,
  selectChatByConversationName,
  chatNotes,
  setChatNotes,
  textingOptOutInfo,
  isLoading,
  handleOpenChat,
  handleCloseChat,
  managementUsers,
  currentRole,
}) => {
  const { t } = useTranslation(['chat'])
  const navigate = useNavigate()
  const [noteValue, setNoteValue] = useState('')
  const [expandedNote, setExpandedNote] = useState(null)
  const [maintenanceRequestDetails, setMaintenanceRequestDetails] = useState(null)
  const [maintenanceRequests, setMaintenanceRequests] = useState(null)
  const isMaintenanceRequestType = selectedChat && isMaintenanceRequestChatType(selectedChat)
  const isMaintenanceGroupChat = selectedChat && isMaintenanceGroupChatType(selectedChat)

  const { mutate, isPending: isExportPending } = useExportTwilioChatCSV()

  const timezone =
    selectedChat && selectedChat.buildingData
      ? selectedChat.buildingData.timezone
      : 'America/Chicago'

  const selectChat = (requestId) => {
    const clientId = selectedChat.channelName.split('_')[0]
    selectChatByConversationName(`${clientId}_MTRQ_${requestId}`)
  }

  const handleCreateNote = () => {
    try {
      API.createChatNote(selectedChat.channelName, { note: noteValue }).then(({ data }) => {
        setChatNotes((prevData) => [...prevData, data])
        setNoteValue('')
      })
    } catch (error) {
      console.error(error.message)
    }
  }

  const goToSpecificMaintenanceRequest = () => {
    const rootRoute = `/dashboard/building-dashboard/maintenance/requests/${selectedChat?.buildingData?.id}/${maintenanceRequestDetails?.id}`
    navigate(rootRoute)
  }

  const handleDownloadChatCSV = () => {
    if (!selectedChat?.id) return
    const trimmed = selectedChat.name?.trim()
    const fileName = trimmed ? `${trimmed}-conversation_summary.csv` : 'conversation_summary.csv'
    mutate({ chatId: selectedChat.id, fileName })
  }

  useEffect(() => {
    if (isMaintenanceRequestType) {
      const reqId = getMaintenanceRequestId(selectedChat.channelName)
      try {
        API.getMaintenanceRequestDetailsForChat(reqId).then(({ data }) => {
          setMaintenanceRequestDetails(data)
        })
      } catch (e) {
        console.error(e.message)
      }
    }
  }, [selectedChat])

  useEffect(() => {
    if (isMaintenanceGroupChat) {
      const clientId = selectedChat.channelName.split('_')[0]
      try {
        API.getMaintenanceRequestsForClient(clientId).then(({ data }) => {
          setMaintenanceRequests(data)
        })
      } catch (e) {
        console.error(e.message)
      }
    }
  }, [isMaintenanceGroupChat, selectedChat])

  useEffect(() => {
    return () => {
      setMaintenanceRequestDetails(null)
    }
  }, [])

  return (
    <div className={`user-information-sidebar ${isLoading ? 'disabled' : ''}`}>
      <div className="avatar-name-container">
        <div className="avatar-container">
          {selectedChat ? (
            <ZoomFrame
              hintSize="2rem"
              src={getAssetLink({ path: 'chitown-avatar/avatar', file: selectedChat.avatar })}
              style={{ borderRadius: '8px', display: 'block' }}
              title={selectedChat.name}
            >
              <img
                src={getAssetLink({ path: 'chitown-avatar/avatar', file: selectedChat.avatar })}
              />
            </ZoomFrame>
          ) : (
            <div className="empty-avatar-container" />
          )}
        </div>
        <div className="name-container">
          {selectedChat ? selectedChat.name : t('chat:conversationInfoBar.noResponder')}
        </div>
        {selectedChat && (
          <div className="chat-status-actions">
            {['Administrator', 'PropertyManager', 'ShiftManager'].includes(currentRole) && (
              <>
                {selectedChat.status === conversationStatusMap.OPEN ? (
                  <button
                    className="button button-primary"
                    onClick={handleCloseChat}
                    disabled={isLoading}
                  >
                    <CheckIcon size={20} style={{ marginRight: 4 }} />
                    {t('chat:conversationInfoBar.markAsClosed')}
                  </button>
                ) : (
                  <button
                    className="button button-primary"
                    onClick={handleOpenChat}
                    disabled={isLoading}
                  >
                    <ArrowUUpLeftIcon size={20} style={{ marginRight: 4 }} />
                    {t('chat:conversationInfoBar.reopenChat')}
                  </button>
                )}
                <button
                  className="button button-primary"
                  onClick={handleDownloadChatCSV}
                  disabled={isExportPending}
                >
                  <DownloadIcon size={20} />
                </button>
              </>
            )}
          </div>
        )}
        {selectedChat && selectedChat?.status !== conversationStatusMap.OPEN && (
          <div className="status-info-container">
            <div className="status-info">
              {selectedChat.status === conversationStatusMap.CLOSED
                ? t('chat:conversationInfoBar.closed')
                : t('chat:conversationInfoBar.archived')}{' '}
              {t('chat:conversationInfoBar.by')}{' '}
              {getUserNameById(
                selectedChat.status === conversationStatusMap.CLOSED
                  ? selectedChat.closedBy
                  : selectedChat.archivedBy,
                selectedChat,
                managementUsers,
                t('chat:conversationInfoBar.systemFallback'),
              )}{' '}
              ·{' '}
              {getFormatedFullDate(
                selectedChat.status === conversationStatusMap.CLOSED
                  ? selectedChat.closedAt
                  : selectedChat.archivedAt,
                timezone,
              )}
            </div>
            <div className="status-explanation">
              {selectedChat.status === conversationStatusMap.CLOSED
                ? t('chat:conversationInfoBar.closedConversationsDeleted')
                : t('chat:conversationInfoBar.archivedChatsDeleted')}
            </div>
          </div>
        )}
      </div>
      {selectedChat ? (
        <React.Fragment>
          <div className="informations-section">
            <div className="section-header">{t('chat:conversationInfoBar.contactInformation')}</div>
            <div className="info">
              <Icon
                icon="email"
                viewBox="-1 -2 24 20"
                fill="none"
                svgProps={{
                  stroke: '#595D62',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
                className="icon"
              />
              {selectedChat.email}
            </div>
            <div className="info">
              <Icon
                icon="phone"
                viewBox="0 0 22 22"
                fill="none"
                svgProps={{
                  stroke: '#595D62',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
                className="icon"
              />
              {selectedChat.phone}
            </div>
            <div className="info">
              <Icon icon="home" viewBox="0 0 21 22" fill="#595D62" className="icon" />
              {selectedChat.address}
            </div>
            <div className="info">
              <Icon
                className="icon"
                icon="propertymanager_inbox"
                viewBox="5 0 8 19"
                fill="#595D62"
                svgProps={{
                  stroke: 'white',
                  strokeWidth: '0.1',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  fillRule: 'evenodd',
                }}
              />
              {selectedChat.buildingData ? selectedChat.buildingData.name : ''}
            </div>
          </div>
          <div className="informations-section">
            <div className="section-header">{t('chat:conversationInfoBar.contactPreferences')}</div>
            <div className="info">
              <div className="info-header">{t('chat:conversationInfoBar.texting')}</div>
              <div className="info-body">
                <StatusPill
                  color={!textingOptOutInfo ? '#36B42B' : '#E53B15'}
                  title={
                    !textingOptOutInfo
                      ? t('chat:conversationInfoBar.optedIn')
                      : t('chat:conversationInfoBar.optedOut')
                  }
                />
              </div>
            </div>
          </div>
          {isMaintenanceGroupChat && maintenanceRequests && maintenanceRequests.length ? (
            <div className="informations-section">
              <div className="section-header">{t('chat:conversationInfoBar.allChatRequests')}</div>
              {maintenanceRequests.map((request) => (
                <div
                  key={request.id}
                  className="maintenance-req-container"
                  onClick={() => selectChat(request.id)}
                >
                  <div className="row">
                    <div className="category-name">{request.category}</div>
                    {renderProperStatusPill(request.status)}
                  </div>
                  <div className="row">
                    <div>{formatInTimeZone(request.createdAt, timezone, 'MM/dd/yyyy h:mm a')}</div>
                  </div>
                  {request.assignee ? (
                    <div className="row">
                      <div>
                        {t('chat:conversationInfoBar.assignedTo')} -{' '}
                        {request.assignee.user.firstName} {request.assignee.user.lastName}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          {isMaintenanceRequestType && maintenanceRequestDetails && (
            <div className="informations-section">
              <div className="section-header">{t('chat:conversationInfoBar.requestDetails')}</div>
              <div className="info">
                <div className="info-header">{t('chat:conversationInfoBar.status')}</div>
                <div className="info-body">
                  {renderProperStatusPill(maintenanceRequestDetails.status)}
                </div>
              </div>
              <div className="info">
                <div className="info-header">{t('chat:conversationInfoBar.id')}</div>
                <div className="info-body">{maintenanceRequestDetails.id}</div>
              </div>
              <div className="info">
                <div className="info-header">{t('chat:conversationInfoBar.date')}</div>
                <div className="info-body">
                  {getFormatedFullDate(maintenanceRequestDetails.createdAt, timezone)}
                </div>
              </div>
              <div className="info">
                <div className="info-header">{t('chat:conversationInfoBar.assignedTo')}</div>
                <div className="info-body">
                  {maintenanceRequestDetails.assignee
                    ? `${maintenanceRequestDetails.assignee.user.firstName} ${maintenanceRequestDetails.assignee.user.lastName}`
                    : '-'}
                </div>
              </div>
              <div className="info">
                <div className="info-header">{t('chat:conversationInfoBar.category')}</div>
                <div className="info-body">{maintenanceRequestDetails.category}</div>
              </div>
              <div className="info" style={{ flexDirection: 'column', alignItems: 'normal' }}>
                <div className="info-header">{t('chat:conversationInfoBar.shortDescription')}</div>
                <div className="info-body">{maintenanceRequestDetails.description}</div>
              </div>
              <div className="info">
                <button
                  type="submit"
                  className="button button-primary"
                  onClick={goToSpecificMaintenanceRequest}
                >
                  {t('chat:conversationInfoBar.goToRequest')}
                </button>
              </div>
            </div>
          )}
          <div className="informations-section">
            <div className="section-header">
              <div>{t('chat:conversationInfoBar.notes')}</div>
              <button
                style={{ width: 90 }}
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                onClick={handleCreateNote}
                disabled={isLoading || !noteValue}
              >
                {t('chat:conversationInfoBar.create')}
              </button>
            </div>
            <TextAreaField
              rows={3}
              name="note"
              maxLength="500"
              placeholder={t('chat:conversationInfoBar.notePlaceholder')}
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              disabled={isLoading}
            />
            {chatNotes
              ? chatNotes
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((note) => (
                    <div key={note.id} className="note-container">
                      <div className="note-time">
                        {formatInTimeZone(note.createdAt, timezone, 'MM/dd/yyyy')}
                      </div>
                      <div className="note-body">{note.note}</div>
                      <div className="note-footer">
                        <div onClick={() => setExpandedNote(note)} className="note-expand">
                          {t('chat:conversationInfoBar.expandNote')}
                        </div>
                        {note.createdBy ? (
                          <div className="note-author">{`${t('chat:conversationInfoBar.by')} ${note.createdBy.firstName} ${note.createdBy.lastName}`}</div>
                        ) : null}
                      </div>
                    </div>
                  ))
              : null}
          </div>
        </React.Fragment>
      ) : null}
      <InformationPopup
        isOpen={!!expandedNote}
        cancelCallback={() => setExpandedNote(null)}
        header={t('chat:conversationInfoBar.note')}
        text={expandedNote ? expandedNote.note : null}
        footer={
          expandedNote
            ? `${formatInTimeZone(expandedNote.createdAt, timezone, 'MM/dd/yyyy')} ${t('chat:conversationInfoBar.by')} ${
                expandedNote.createdBy.firstName
              } ${expandedNote.createdBy.lastName}`
            : null
        }
      />
    </div>
  )
}

ConversationInfoBar.propTypes = {
  selectedChat: PropTypes.object,
  selectChatByConversationName: PropTypes.func,
  setChatNotes: PropTypes.func,
  chatNotes: PropTypes.array,
  textingOptOutInfo: PropTypes.bool,
  isLoading: PropTypes.bool,
  handleOpenChat: PropTypes.func,
  handleCloseChat: PropTypes.func,
  managementUsers: PropTypes.array,
  currentRole: PropTypes.string,
}

export default ConversationInfoBar
